import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// ============================================================================
// Types
// ============================================================================

export interface DesignItemSummary {
  designItemId: string;
  title: string;
  description: string | null;
  status: string;
  sourceType: string;
  figmaUrl: string | null;
  thumbnailPath: string | null;
  uploadedFilePath: string | null;
  externalUrl: string | null;
  reviewRound: number;
  // CHG-133 Step 7
  assignee: { id: string; name: string; avatarUrl: string | null; displayColor?: string | null; nickname?: string | null; firstName?: string | null; lastName?: string | null } | null;
  createdBy: { id: string; name: string; avatarUrl: string | null; displayColor?: string | null; nickname?: string | null; firstName?: string | null; lastName?: string | null };
  figmaFile: { figmaFileId: string; fileKey: string; fileName: string } | null;
  reviewSummary: { totalReviewers: number; approved: number; pending: number };
  needsReReview: boolean;
  handoffChecklist: HandoffChecklistItem[] | null;
  handoffAt: string | null;
  handoffById: string | null;
  handoffBy?: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface DesignItemDetail extends DesignItemSummary {
  issueLinks: {
    id: string;
    issueId: string;
    issueKey: string;
    issueTitle: string;
    issueStatusId: string;
    linkType: string;
  }[];
  reviews: {
    reviewId: string;
    reviewer: { id: string; name: string; avatarUrl: string | null };
    status: string;
    comment: string | null;
    reviewRound: number;
    createdAt: string;
    updatedAt: string;
  }[];
  recentVersions: {
    versionId: string;
    figmaVersionId: string | null;
    label: string | null;
    thumbnailPath: string | null;
    isPinned: boolean;
    capturedAt: string | null;
    createdAt: string;
  }[];
  pinnedVersion: {
    versionId: string;
    label: string | null;
    thumbnailPath: string | null;
  } | null;
  isPinnedVersionOutdated: boolean;
  needsReReview: boolean;
}

export interface DesignItemListResponse {
  items: DesignItemSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateDesignItemInput {
  title: string;
  description?: string;
  sourceType: 'FIGMA' | 'UPLOAD' | 'EXTERNAL_URL';
  figmaUrl?: string;
  figmaFileKey?: string;
  figmaNodeId?: string;
  externalUrl?: string;
  assigneeId?: string;
  issueIds?: { issueId: string; linkType: string }[];
}

// ============================================================================
// API Functions
// ============================================================================

export async function fetchDesignItems(
  projectId: string,
  params?: Record<string, string | number | undefined>,
): Promise<DesignItemListResponse> {
  const res = await api.get<ApiResponse<DesignItemListResponse>>(
    `/projects/${projectId}/design-items`,
    { params },
  );
  return res.data.data!;
}

export async function fetchDesignItemDetail(
  projectId: string,
  designItemId: string,
): Promise<DesignItemDetail> {
  const res = await api.get<ApiResponse<DesignItemDetail>>(
    `/projects/${projectId}/design-items/${designItemId}`,
  );
  return res.data.data!;
}

export async function createDesignItem(
  projectId: string,
  data: FormData | CreateDesignItemInput,
): Promise<DesignItemSummary> {
  const isFormData = data instanceof FormData;
  const res = await api.post<ApiResponse<DesignItemSummary>>(
    `/projects/${projectId}/design-items`,
    data,
    isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
  );
  return res.data.data!;
}

export async function updateDesignItem(
  projectId: string,
  designItemId: string,
  data: Record<string, unknown>,
): Promise<DesignItemSummary> {
  const res = await api.put<ApiResponse<DesignItemSummary>>(
    `/projects/${projectId}/design-items/${designItemId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteDesignItem(projectId: string, designItemId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/design-items/${designItemId}`);
}

export async function changeDesignItemStatus(
  projectId: string,
  designItemId: string,
  status: string,
  comment?: string,
): Promise<unknown> {
  const res = await api.patch<ApiResponse<unknown>>(
    `/projects/${projectId}/design-items/${designItemId}/status`,
    { status, comment },
  );
  return res.data.data!;
}

export async function refreshThumbnail(
  projectId: string,
  designItemId: string,
): Promise<{ thumbnailPath: string | null }> {
  const res = await api.post<ApiResponse<{ thumbnailPath: string | null }>>(
    `/projects/${projectId}/design-items/${designItemId}/thumbnail/refresh`,
  );
  return res.data.data!;
}

export async function addDesignLink(
  projectId: string,
  designItemId: string,
  issueId: string,
  linkType: string,
): Promise<unknown> {
  const res = await api.post<ApiResponse<unknown>>(
    `/projects/${projectId}/design-items/${designItemId}/links`,
    { issueId, linkType },
  );
  return res.data.data!;
}

export async function removeDesignLink(
  projectId: string,
  designItemId: string,
  linkId: string,
): Promise<void> {
  await api.delete(`/projects/${projectId}/design-items/${designItemId}/links/${linkId}`);
}

export interface IssueDesignItem {
  linkId: string;
  linkType: string;
  designItemId: string;
  title: string;
  status: string;
  sourceType: string;
  thumbnailPath: string | null;
  figmaUrl: string | null;
  uploadedFilePath: string | null;
  externalUrl: string | null;
  needsReReview?: boolean;
}

export async function fetchIssueDesignItems(
  projectId: string,
  issueId: string,
): Promise<IssueDesignItem[]> {
  const res = await api.get<ApiResponse<IssueDesignItem[]>>(
    `/projects/${projectId}/issues/${issueId}/design-items`,
  );
  return res.data.data!;
}

// Phase 2: Handoff APIs

export interface HandoffChecklistItem {
  label: string;
  checked: boolean;
  required: boolean;
}

export async function updateHandoffChecklist(
  projectId: string,
  designItemId: string,
  checklist: HandoffChecklistItem[],
): Promise<unknown> {
  const res = await api.put<ApiResponse<unknown>>(
    `/projects/${projectId}/design-items/${designItemId}/handoff-checklist`,
    { checklist },
  );
  return res.data.data!;
}

export async function executeHandoff(
  projectId: string,
  designItemId: string,
): Promise<{ handedOff: boolean; pinnedLabel: string | null }> {
  const res = await api.post<ApiResponse<{ handedOff: boolean; pinnedLabel: string | null }>>(
    `/projects/${projectId}/design-items/${designItemId}/handoff`,
  );
  return res.data.data!;
}

export async function addChangeNote(
  projectId: string,
  designItemId: string,
  note: string,
  dismissFlag?: boolean,
): Promise<unknown> {
  const res = await api.post<ApiResponse<unknown>>(
    `/projects/${projectId}/design-items/${designItemId}/change-note`,
    { note, dismissFlag },
  );
  return res.data.data!;
}

export async function restoreHandoff(
  projectId: string,
  designItemId: string,
): Promise<unknown> {
  const res = await api.post<ApiResponse<unknown>>(
    `/projects/${projectId}/design-items/${designItemId}/restore-handoff`,
  );
  return res.data.data!;
}
